export enum PricingSetUpEnum {
    noProcessing = 'NO_PROCESSING_FEE',
    single = 'SINGLE_PRICING',
    tiered = 'TIERED_PRICING',
    notConfigured = 'NOT_CONFIGURED',
}

export enum PricingTypeEnum {
    general = 'General Acceptance',
    custom = 'Custom Pricing',
}

export interface PricingRange {
    rangeId: number;
    pricingTableId?: string;
    minimum: number;
    maximum?: number;
    cost?: number;
    feePercentage?: number;
    fee: number;
}

export interface CustomReferenceNumber {
    rangeId?: number;
    referenceNumber: string;
    referenceDescription: string;
    isActive: boolean;
}

export interface Pricing {
    label: string;
    pricingType: string;
    pricingTableId?: string;
    pricingSetUp: PricingSetUpEnum;
    pricingRanges: PricingRange[];
    customReferenceNumbers: CustomReferenceNumber[];
    includeGST?: boolean;
}

export const PRICING_SETUP_OPTIONS = [
    {
        type: PricingSetUpEnum.noProcessing,
        name: 'No Supplier Fee',
    },
    {
        type: PricingSetUpEnum.single,
        name: 'Single Pricing',
    },
    {
        type: PricingSetUpEnum.tiered,
        name: 'Tiered Pricing',
    },
];

export interface PricingRangePreview {
    rangeId: number;
    transactionAmount: number;
    minimum: number;
    cost?: number;
    fee: number;
    recoveryFee?: number;
    settlementAmount: number;
    gst: boolean;
}

function normNum(val: any): number | null {
    if (val === null || val === undefined || val === '') return null;
    const n = Number(val);
    return isNaN(n) ? null : n;
}

function normStr(val: any): string {
    return (val ?? '').toString().trim();
}

export function isPricingRangeEqual(o: PricingRange, c: PricingRange): boolean {
    if (!o || !c) return false;
    return (
        normNum(o.minimum) === normNum(c.minimum) &&
        normNum(o.maximum) === normNum(c.maximum) &&
        normNum(o.cost) === normNum(c.cost) &&
        normNum(o.fee) === normNum(c.fee) &&
        normNum(o.feePercentage) === normNum(c.feePercentage)
    );
}

export function isCustomRefEqual(o: CustomReferenceNumber, c: CustomReferenceNumber): boolean {
    if (!o || !c) return false;
    return (
        normNum(o.rangeId) === normNum(c.rangeId) &&
        normStr(o.referenceNumber) === normStr(c.referenceNumber) &&
        normStr(o.referenceDescription) === normStr(c.referenceDescription) &&
        Boolean(o.isActive) === Boolean(c.isActive)
    );
}

/**
 * Compares two complete Pricing configurations for a single tab
 */
export function isTabPricingEqual(original?: Pricing | null, current?: Pricing | null): boolean {
    if (!original && !current) return true;
    if (!original || !current) return false;

    // 1. Scalar Top-Level Properties
    if (
        normStr(original.label) !== normStr(current.label) ||
        normStr(original.pricingType) !== normStr(current.pricingType) ||
        normStr(original.pricingTableId) !== normStr(current.pricingTableId) ||
        original.pricingSetUp !== current.pricingSetUp ||
        Boolean(original.includeGST) !== Boolean(current.includeGST)
    ) {
        return false;
    }

    // 2. Pricing Ranges Array
    const origRanges = original.pricingRanges ?? [];
    const currRanges = current.pricingRanges ?? [];
    if (origRanges.length !== currRanges.length) return false;
    if (!origRanges.every((r, idx) => isPricingRangeEqual(r, currRanges[idx]))) {
        return false;
    }

    // 3. Custom Reference Numbers Array
    const origRefs = original.customReferenceNumbers ?? [];
    const currRefs = current.customReferenceNumbers ?? [];
    if (origRefs.length !== currRefs.length) return false;
    if (!origRefs.every((ref, idx) => isCustomRefEqual(ref, currRefs[idx]))) {
        return false;
    }

    return true;
}